import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';

/**
 * Custom TranslateLoader that loads and merges multiple JSON files
 * from a modular directory structure.
 *
 * Features:
 * - Modular translation files organized by feature
 * - In-memory caching to prevent redundant HTTP requests
 * - Graceful fallback for missing files
 *
 * Structure:
 * assets/i18n/
 *   en/
 *     common.json
 *     home.json
 *     auth.json
 *     admin/
 *       shared.json
 *       academic-years.json
 *       ...
 *   ar/ (same structure)
 *   fr/ (same structure)
 */
export class MultiFileTranslateLoader implements TranslateLoader {
  // Cache for storing loaded translations in memory
  private cache = new Map<string, Observable<any>>();

  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json'
  ) {}

  /**
   * Define all translation files to load for each language
   * Organized by feature/module for easy maintenance
   */
  private getTranslationFiles(): string[] {
    return [
      // Core/Shared translations
      'common',
      'home',
      'auth',
      'student',

      // Admin module translations
      'admin/shared',
      'admin/academic-years',
      'admin/classes',
      'admin/subjects',
      'admin/offers',
      'admin/users',
      'admin/payments',
      'admin/pedagogic',
      'admin/timetable'
    ];
  }

  /**
   * Load all translation files and merge them into a single object
   * Implements caching to prevent redundant HTTP requests
   */
  getTranslation(lang: string): Observable<any> {
    // Check cache first
    const cacheKey = lang;
    if (this.cache.has(cacheKey)) {
      console.log(`[i18n] Using cached translations for language: ${lang}`);
      return this.cache.get(cacheKey)!;
    }

    const files = this.getTranslationFiles();

    // Create an array of HTTP requests for each file
    const requests = files.map(file => {
      const path = `${this.prefix}${lang}/${file}${this.suffix}`;
      return this.http.get(path).pipe(
        // If file doesn't exist, return empty object (graceful fallback)
        catchError(() => {
          console.warn(`[i18n] Translation file not found: ${path}`);
          return of({});
        })
      );
    });

    // Execute all requests in parallel and merge results
    const translation$ = forkJoin(requests).pipe(
      map((responses: any[]) => {
        // Deep merge all translation objects
        return this.mergeTranslations(responses);
      }),
      // Cache the result using shareReplay
      shareReplay(1)
    );

    // Store in cache
    this.cache.set(cacheKey, translation$);
    console.log(`[i18n] Loading translations for language: ${lang}`);

    return translation$;
  }

  /**
   * Deep merge multiple translation objects
   * Later objects override earlier ones for the same keys
   */
  private mergeTranslations(objects: any[]): any {
    const result: any = {};

    for (const obj of objects) {
      this.deepMerge(result, obj);
    }

    return result;
  }

  /**
   * Recursively merge source into target
   */
  private deepMerge(target: any, source: any): void {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] instanceof Object && !Array.isArray(source[key])) {
          // If both are objects, merge recursively
          if (!target[key]) {
            target[key] = {};
          }
          this.deepMerge(target[key], source[key]);
        } else {
          // Otherwise, override
          target[key] = source[key];
        }
      }
    }
  }
}

/**
 * Factory function for AOT compilation
 */
export function MultiFileTranslateLoaderFactory(http: HttpClient) {
  return new MultiFileTranslateLoader(http);
}
